/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  DynamicPricingInitialProperties,
  DynamicPricingProperties,
  FetchPricingProperties,
  RequestPricingInterface,
} from '../../interface/dynamic-pricing.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import dynamicPricing from '../../utils/api/dynamic-pricing';

// a thunk to fetch users
export const fetchRejectedPricing = createAsyncThunk<
  FetchPricingProperties,
  RequestPricingInterface
>('rejected-pricing/fetchRejectedPricing', async (data: RequestPricingInterface, { dispatch }) => {
  try {
    const response = await dynamicPricing.fetchDynamicPricing(
      removeEmptyKeys({ ...data, status: 'rejected' }),
    );
    return response.data;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to fetch devices filters
export const fetchListDynamicPricingFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('rejected-pricing/fetchListDynamicPricingFilters', async (_, { dispatch }) => {
  try {
    const response = await dynamicPricing.fetchDynamicPricingFilterList();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DynamicPricingInitialProperties = {
  pricing: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

// Create the slice
export const rejectedPricingSlice = createSlice({
  name: 'rejected-pricing',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state state
     * @param {any} action action
     */
    updateRejectedDynamicPricingFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state state
     */
    disableRejectedDynamicPricingFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state state
     * @param {any} action action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchRejectedPricing.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRejectedPricing.fulfilled, (state, action) => {
        state.loading = false;
        state.pricing = action.payload?.results?.length
          ? action.payload.results?.map((eachPricing: DynamicPricingProperties) => ({
              ...eachPricing,
              createdAt: eachPricing.createdAt ? ConvertUTCtoDate(eachPricing?.createdAt) : '',
              electricityCostByUtilityProvider:
                eachPricing.electricityCostByUtilityProvider === null
                  ? 'Not Available'
                  : eachPricing.electricityCostByUtilityProvider,
            }))
          : [];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchRejectedPricing.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListDynamicPricingFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListDynamicPricingFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchListDynamicPricingFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});
// Export the actions
export const {
  updateRejectedDynamicPricingFilters,
  disableRejectedDynamicPricingFiltersApplied,
  setSortData,
} = rejectedPricingSlice.actions;
// Export the reducer
export default rejectedPricingSlice.reducer;
